import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, Gauge, Loader2, Scale } from "lucide-react";
import ActiveCardShell from "../ActiveCardShell";
import { sleep, RunButton } from "./shared";

const RULES = [
  ["MCC risk tier", "5812 · low risk"],
  ["Monthly volume vs. appetite", "Within $50k–$500k band"],
  ["Chargeback history", "0.21% · under 0.9% threshold"],
  ["Time in business", "6 years"],
];
const TARGET = 82;

function RiskGauge({ value }) {
  const r = 52;
  const c = Math.PI * r;
  return (
    <svg viewBox="0 0 120 70" className="w-44 h-28" data-testid="veroogle-t2400-gauge">
      <path d="M8 62 A52 52 0 0 1 112 62" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10" strokeLinecap="round" />
      <path d="M8 62 A52 52 0 0 1 112 62" fill="none" stroke="url(#uwg)" strokeWidth="10" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - value / 100)} style={{ transition: "stroke-dashoffset 120ms linear" }} />
      <defs><linearGradient id="uwg"><stop offset="0" stopColor="#f43f5e" /><stop offset="0.5" stopColor="#f59e0b" /><stop offset="1" stopColor="#10b981" /></linearGradient></defs>
      <text x="60" y="58" textAnchor="middle" className="fill-white font-mono" fontSize="20" fontWeight="700">{value}</text>
    </svg>
  );
}

export default function UnderwritingCard({ merchant, onApproved }) {
  const [score, setScore] = useState(0);
  const [rules, setRules] = useState(0);
  const [decided, setDecided] = useState(false);
  const [running, setRunning] = useState(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const run = async () => {
    setRunning(true); setScore(0); setRules(0); setDecided(false);
    for (let v = 0; v <= TARGET; v += 3) { await sleep(28); if (alive.current) setScore(Math.min(v, TARGET)); }
    setScore(TARGET);
    for (let i = 1; i <= RULES.length; i += 1) { await sleep(420); if (alive.current) setRules(i); }
    await sleep(500);
    if (!alive.current) return;
    setDecided(true);
    setRunning(false);
    onApproved?.(merchant);
  };

  return (
    <ActiveCardShell id="T2400cr" kicker="UNDERWRITING" title="Automated Underwriting & Risk Scoring" Icon={Scale}
      actions={<RunButton tid="veroogle-t2400-run" running={running} onClick={run} Icon={running ? Loader2 : Gauge} label="Run underwriting" />}>
      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-5 items-center">
        <div className="flex flex-col items-center">
          <RiskGauge value={score} />
          <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-400 -mt-1">Risk score · higher is safer</div>
        </div>
        <div className="space-y-2">
          {RULES.map(([label, detail], i) => (
            <div key={label} data-testid={`veroogle-t2400-rule-${i}`}
              className={`flex items-center gap-3 rounded-lg border px-3 py-2 transition-opacity duration-300 ${i < rules ? "border-emerald-500/30 bg-emerald-500/5 opacity-100" : "border-white/10 opacity-40"}`}>
              <CheckCircle2 className={`w-4 h-4 ${i < rules ? "text-emerald-400" : "text-slate-600"}`} />
              <span className="text-sm text-white flex-1">{label}</span>
              <span className="text-xs text-slate-400">{i < rules ? detail : "—"}</span>
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <div className="rounded-lg border border-white/10 px-3 py-2">
              <div className="font-mono text-[9px] tracking-[0.18em] uppercase text-slate-500">Approved limit</div>
              <div className="font-mono text-white" data-testid="veroogle-t2400-limit">{decided ? "$250,000 / mo" : "—"}</div>
            </div>
            {decided && (
              <div data-testid="veroogle-t2400-decision"
                className="max-w-full break-words px-4 py-2 rounded-lg bg-emerald-500 text-navy-950 font-mono text-xs sm:text-sm font-bold tracking-[0.14em] sm:tracking-[0.2em] uppercase shadow-[0_0_24px_rgba(16,185,129,0.4)] animate-in zoom-in-75 fade-in duration-300">
                Approved · {merchant.name}
              </div>
            )}
          </div>
        </div>
      </div>
    </ActiveCardShell>
  );
}
